/**
 * Helper functions for Instagram Reels integration
 */

/**
 * Extracts Instagram Reel ID from various Instagram URL formats
 * @param url - Instagram URL (reel, post, or short URL)
 * @returns Reel ID or null if not found
 */
export const extractInstagramReelId = (url: string): string | null => {
  if (!url) return null;

  // Clean the URL
  const cleanUrl = url.trim();

  // Instagram Reel URL patterns
  const patterns = [
    // https://www.instagram.com/reel/ABC123/
    /instagram\.com\/reel\/([A-Za-z0-9_-]+)/,
    // https://www.instagram.com/p/ABC123/ (can be a reel)
    /instagram\.com\/p\/([A-Za-z0-9_-]+)/,
    // Short URL format
    /instagram\.com\/reels\/([A-Za-z0-9_-]+)/,
  ];

  for (const pattern of patterns) {
    const match = cleanUrl.match(pattern);
    if (match && match[1]) {
      return match[1];
    }
  }

  return null;
};

/**
 * Validates if a URL is a valid Instagram Reel URL
 * @param url - URL to validate
 * @returns boolean indicating if URL is valid
 */
export const isValidInstagramReelUrl = (url: string): boolean => {
  if (!url) return false;

  const reelId = extractInstagramReelId(url);
  return reelId !== null;
};

/**
 * Generates Instagram embed URL from reel ID
 * @param reelId - Instagram Reel ID
 * @returns Embed URL
 */
export const generateInstagramEmbedUrl = (reelId: string): string => {
  return `https://www.instagram.com/reel/${reelId}/embed/`;
};

/**
 * Generates Instagram share URL from reel ID
 * @param reelId - Instagram Reel ID
 * @returns Share URL
 */
export const generateInstagramShareUrl = (reelId: string): string => {
  return `https://www.instagram.com/reel/${reelId}/`;
};

/**
 * Extracts reel ID from Instagram embed URL
 * @param embedUrl - Instagram embed URL
 * @returns Reel ID or null
 */
export const extractReelIdFromEmbedUrl = (embedUrl: string): string | null => {
  const match = embedUrl.match(/instagram\.com\/reel\/([A-Za-z0-9_-]+)\/embed/);
  return match ? match[1] : null;
};

/**
 * Validates Instagram Reel ID format
 * @param reelId - Reel ID to validate
 * @returns boolean indicating if ID is valid
 */
export const isValidReelId = (reelId: string): boolean => {
  if (!reelId) return false;

  // Instagram IDs are typically 11 characters, alphanumeric with underscores and hyphens
  const reelIdPattern = /^[A-Za-z0-9_-]{8,15}$/;
  return reelIdPattern.test(reelId);
};

/**
 * Normalizes Instagram URL to standard format
 * @param url - Instagram URL to normalize
 * @returns Normalized URL or null if invalid
 */
export const normalizeInstagramUrl = (url: string): string | null => {
  const reelId = extractInstagramReelId(url);
  if (!reelId) return null;

  return generateInstagramShareUrl(reelId);
};
